import { apiFetch } from './client.js';

/**
 * Teams for the MPC coach (028; mockup *Teams*, approved 25 October): every
 * team, one team with its members, finding people, adding them as coaches or
 * players, the head coach, and a player's number and positions.
 */

type GetToken = () => Promise<string | null>;

export interface TeamRow {
  team_id: string;
  name: string;
  /** A one-hour link to the logo, or null (stage 2). */
  logo_url: string | null;
  head_coach: string | null;
  coaches: number;
  players: number;
}

export interface TeamMember {
  client_id: string;
  first_name: string;
  last_name: string;
  role: 'head_coach' | 'assistant' | 'player';
  adult: boolean;
  signed_up: boolean;
  /** Board 5a (26 October): their own account, or a family responsible for them, signs in. */
  family_signed_up: boolean;
  /** Signed up, or their open invitation has an address to send to. */
  has_email: boolean;
  /** When their invitation last went out, ISO, or null. */
  invited_at: string | null;
  jersey_number: string | null;
  positions: string[];
  /** A one-hour link to the photo, or null (stage 2). */
  photo_url: string | null;
}

export interface Team {
  team_id: string;
  name: string;
  logo_url: string | null;
  members: TeamMember[];
}

export interface TeamPersonHit {
  client_id: string;
  name: string;
  adult: boolean;
  detail: string;
}

/** As the API takes them (save-player-detail.ts), in the order of the buttons. */
export const POSITIONS = ['P', 'C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'UT'] as const;

const json = (body: unknown) => ({ method: 'POST', body: JSON.stringify(body) });

export function listTeams(getToken: GetToken, query: string): Promise<{ teams: TeamRow[] }> {
  return apiFetch(`/api/coach/teams?q=${encodeURIComponent(query)}`, getToken);
}

export function createTeam(getToken: GetToken, name: string): Promise<{ team_id: string }> {
  return apiFetch('/api/coach/teams', getToken, json({ name }));
}

export function readTeam(getToken: GetToken, teamId: string): Promise<Team> {
  return apiFetch(`/api/coach/teams/${encodeURIComponent(teamId)}`, getToken);
}

/** Send N in Select mode (board 5a): each picked family's sign-up invitation, sent or sent again. */
export function sendTeamInvitations(
  getToken: GetToken,
  teamId: string,
  clientIds: string[],
): Promise<{ sent: string[]; skipped: { client_id: string; reason: string }[] }> {
  return apiFetch(`/api/coach/teams/${encodeURIComponent(teamId)}/invitations`, getToken, json({ client_ids: clientIds }));
}

export function renameTeam(getToken: GetToken, teamId: string, name: string): Promise<Team> {
  return apiFetch(`/api/coach/teams/${encodeURIComponent(teamId)}`, getToken, { method: 'PUT', body: JSON.stringify({ name }) });
}

export function searchTeamPeople(getToken: GetToken, query: string): Promise<{ people: TeamPersonHit[] }> {
  return apiFetch(`/api/coach/team-people?q=${encodeURIComponent(query)}`, getToken);
}

export function addTeamMember(getToken: GetToken, teamId: string, clientId: string, role: 'coach' | 'player'): Promise<unknown> {
  return apiFetch(`/api/coach/teams/${encodeURIComponent(teamId)}/members`, getToken, json({ client_id: clientId, role }));
}

/** Someone not on the site, made and added (25 October); Clients' field names. */
export function addNewTeamMember(
  getToken: GetToken,
  teamId: string,
  body: Record<string, unknown>,
): Promise<{ adult_account_id: string; client_id: string; emailed: boolean }> {
  return apiFetch(`/api/coach/teams/${encodeURIComponent(teamId)}/new-members`, getToken, json(body));
}

export function makeHeadCoach(getToken: GetToken, teamId: string, clientId: string): Promise<unknown> {
  return apiFetch(`/api/coach/teams/${encodeURIComponent(teamId)}/members/${encodeURIComponent(clientId)}`, getToken, {
    method: 'PUT',
    body: JSON.stringify({ role: 'head_coach' }),
  });
}

export function removeTeamMember(getToken: GetToken, teamId: string, clientId: string): Promise<unknown> {
  return apiFetch(`/api/coach/teams/${encodeURIComponent(teamId)}/members/${encodeURIComponent(clientId)}`, getToken, {
    method: 'DELETE',
  });
}

export function savePlayerDetails(getToken: GetToken, clientId: string, jerseyNumber: string, positions: string[]): Promise<unknown> {
  return apiFetch(`/api/coach/players/${encodeURIComponent(clientId)}/details`, getToken, {
    method: 'PUT',
    body: JSON.stringify({ jersey_number: jerseyNumber, positions }),
  });
}

/** A logo (512 PNG) or photo (256 JPEG) made on the phone (team-image-file.ts), as base64; null removes it. */
export function saveTeamImage(getToken: GetToken, path: string, image: string | null): Promise<{ url: string | null }> {
  return apiFetch(path, getToken, image === null ? { method: 'DELETE' } : { method: 'PUT', body: JSON.stringify({ image }) });
}

export const teamLogoPath = (teamId: string) => `/api/coach/teams/${encodeURIComponent(teamId)}/logo`;
export const playerPhotoPath = (clientId: string) => `/api/coach/players/${encodeURIComponent(clientId)}/photo`;

/** "SF" for "Sample Fastpitch 12U": the logo's stand-in on a team without one. */
export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter((word) => /^[A-Za-z]/.test(word))
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');
}
